import { useState } from 'react';
import { LOGO_SMALL } from '@/assets/logo';
import { LogIn, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('vertex_admin_token', res.data.token);
      localStorage.setItem('vertex_admin_email', res.data.email);
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-vertex-gradient flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-vertex-radial" />
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={LOGO_SMALL} alt="Vertex" className="h-12 w-12 rounded-xl" />
            <div>
              <span className="text-white font-display font-bold text-2xl tracking-tight">VERTEX</span>
              <span className="text-vertex-400 font-display font-medium text-xs block -mt-1 tracking-widest">ADMIN PANEL</span>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div className="glass rounded-2xl p-8" data-testid="admin-login-form">
          <h2 className="text-xl font-display font-bold text-white mb-1">Welcome back</h2>
          <p className="text-gray-400 text-sm mb-6">Sign in to access your dashboard</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-gray-400 text-xs font-medium mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all"
                placeholder="admin@vertex.com"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium mb-1.5 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 px-4 py-2.5 rounded-xl">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full btn-primary text-sm disabled:opacity-50"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</>
              ) : (
                <><LogIn className="w-4 h-4 mr-2" /> Sign In</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
